import React, { useEffect, useMemo, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { api, unwrap } from '@/lib/api';
import API_URL from '@/lib/apiConfig';
import LoadingSpinner from '../../widgets/LoadingSpinner';
import PostmortemViewerModal from './PostmortemViewerModal';

export default function PostmortemsView({ project }) {
  const [waves, setWaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);

  const projectId = project?.project_id ?? project?._id;

  useEffect(() => {
    if (!projectId) return;
    setLoading(true);
    api.get(`${API_URL}/api/projects/waves?project_id=${projectId}`)
      .then((res) => res.json())
      .then((data) => setWaves(unwrap(data)))
      .finally(() => setLoading(false));
  }, [projectId]);

  const list = useMemo(
    () =>
      waves
        .filter((w) => w.postmortem != null)
        .sort((a, b) => b.wave_number - a.wave_number),
    [waves],
  );

  if (loading) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <section data-testid="postmortems-view" className="px-6 py-4">
      <header className="mb-4 flex items-baseline gap-3">
        <h2 className="text-sm font-bold text-boss-text">Postmortems</h2>
        <span className="text-[11px] text-boss-text-muted">{list.length} recorded</span>
      </header>

      {list.length === 0 ? (
        <div className="rounded-md border border-dashed border-boss-border p-8 text-center" data-testid="postmortems-empty">
          <p className="text-sm text-boss-text-muted">No postmortems yet. They appear here once a wave is closed.</p>
        </div>
      ) : (
        <ul className="rounded-md border border-boss-border bg-boss-surface overflow-hidden">
          {list.map((w) => (
            <li
              key={w.wave_id ?? w._id}
              className="flex items-center gap-3 px-4 py-3 border-b last:border-b-0 border-boss-border"
              data-testid={`postmortem-row-${w.wave_number}`}
            >
              <span className="font-mono text-xs font-bold text-boss-text">WAVE {w.wave_number}</span>
              <span className="flex-1 truncate text-sm text-boss-text">{w.goal}</span>
              <span className="text-[11px] text-boss-text-muted">
                {(w.start_date && w.end_date)
                  ? `${format(parseISO(w.start_date), 'MMM d')} – ${format(parseISO(w.end_date), 'MMM d')}`
                  : '—'}
              </span>
              <button
                type="button"
                onClick={() => setOpen(w)}
                className="btn-secondary h-7 px-3 text-xs"
                data-testid={`postmortem-view-${w.wave_number}`}
              >
                View
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* Viewer modal — renders nothing when `open` is null */}
      <PostmortemViewerModal
        wave={open}
        projectName={project?.name}
        onClose={() => setOpen(null)}
      />
    </section>
  );
}
